import { useEffect, useState } from 'react';

export const DEFAULT_DEBOUNCE_MS = 350;

export type UseDebouncedValueOptions = {
  /** Delay in ms before the debounced value updates. Default 350 (aligned with Risks / TestRuns). */
  delayMs?: number;
  /** When false, debounced value tracks `value` immediately. Default true. */
  enabled?: boolean;
  /** Trim string values before emitting. Default true. */
  trim?: boolean;
};

/**
 * Returns a debounced copy of `value`. Use for search boxes and other inputs that drive API queries.
 */
export function useDebouncedValue<T>(value: T, options: UseDebouncedValueOptions = {}): T {
  const { delayMs = DEFAULT_DEBOUNCE_MS, enabled = true, trim = true } = options;
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (!enabled) {
      setDebounced(value);
      return undefined;
    }

    const emit = () => {
      if (typeof value === 'string' && trim) {
        setDebounced(value.trim() as T);
      } else {
        setDebounced(value);
      }
    };

    const timer = window.setTimeout(emit, delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs, enabled, trim]);

  return debounced;
}
